import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import Header from '../components/header';
import BaseURL from '../assets/contants/BaseURL';
import axios from 'axios';
import ExcelJS from 'exceljs';

const formatDateForInput = (date) => {
  const pad = (num) => num.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const ProductionDetails = () => {
  const [productionData, setProductionData] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [machinesData, setMachinesData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [productsData, setProductsData] = useState([]);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editType, setEditType] = useState('');
  const [editFormData, setEditFormData] = useState({
    id: null,
    production_date: '',
    machine_group: '',
    from_date_time: '',
    to_date_time: '',
    product: null,
    product_name: '',
    actual_production: ''
  });
  const editModalRef = useRef();
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    from_date_time: '',
    to_date_time: '',
    machine_group: '',
    production_date: '',
    interval_type: 'daily'
  });
  const modalRef = useRef();
  const deleteModalRef = useRef(null);
  const [pagination, setPagination] = useState({
    total_pages: 1,
    current_page: 1,
    previous: null,
    next: null,
    total_items: 0,
    items_per_page: 10
  });
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const superuserStatus = sessionStorage.getItem('superuser') === 'true';
    setIsSuperuser(superuserStatus);
  }, []);

  const fetchInitialProductionData = async () => {
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.get(`${BaseURL}meters/production/`, {
        headers: {
          'Authorization': `Token ${token}`
        }
      });
      processApiResponse(response.data);
    } catch (error) {
      console.error('Error fetching production details:', error);
    }
  };

  const fetchProductionData = async (page = 1, fromDate = "", toDate = "", machine_id = "") => {
    const token = sessionStorage.getItem('token');
    let url = `${BaseURL}meters/production/?page=${page}`;

    if (fromDate) {
      url += `&fromdate=${fromDate}`;
    }
    if (toDate) {
      url += `&todate=${toDate}`;
    }
    if (machine_id) {
      url += `&machine_id=${machine_id}`;
    }

    try {
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Token ${token}`
        }
      });
      processApiResponse(response.data);
    } catch (error) {
      console.error('Error fetching production details:', error);
    }
  };

  useEffect(() => {
    if (fromDate || toDate || searchQuery) {
      fetchProductionData(1, fromDate, toDate, searchQuery);
    }
  }, [fromDate, toDate, searchQuery]);

  const processApiResponse = (data) => {
    const results = data.results || data;
    const paginationData = data.pagination || {
      total_pages: 1,
      current_page: 1,
      previous: null,
      next: null,
      total_items: Array.isArray(data) ? data.length : 0,
      items_per_page: Array.isArray(data) ? data.length : 10
    };

    if (results.length > 0) {
      const products = Object.keys(results[0]).filter(
        key => !['Date', 'From', 'To', 'Department', 'kWh', 'Cost'].includes(key)
      );
      setProductsList(products);
    }

    const sortedResults = [...results].sort((a, b) => {
      const dateA = new Date(`${a.Date}T${a.From}`);
      const dateB = new Date(`${b.Date}T${b.From}`);
      return dateB - dateA;
    });

    const dataWithIds = sortedResults.map((item, index) => ({
      ...item,
      id: `${item.Date}-${item.Department}-${index}`
    }));

    setProductionData(dataWithIds);
    setPagination(paginationData);
  };

  useEffect(() => {
    fetchInitialProductionData();
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    axios.get(`${BaseURL}meters/enpi-machine/`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      )
      .then(response => {
        setMachinesData(response.data);
      })
      .catch(error => {
        console.error('Error fetching machine details:', error);
      });
  }, []);

  const getMachineName = (machineId) => {
    const machine = machinesData.find(item => String(item.id) === String(machineId));
    return machine ? machine.machine_name : "Unknown Machine";
  };


  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchClick = () => {
    if (searchQuery) {
      fetchProductionData(1, fromDate, toDate, searchQuery);
    } else {
      fetchProductionData(1, fromDate, toDate);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.total_pages) {
      fetchProductionData(page, fromDate, toDate, searchQuery);
    }
  };

  useEffect(() => {
    axios.get(`${BaseURL}meters/products/`)
      .then(response => {
        setProductsData(response.data);
      })
      .catch(error => {
        console.error('Error fetching products:', error);
      });
  }, []);

  const getProductUnit = (productName) => {
    const product = productsData.find(p => p.product_name === productName);
    return product ? product.unit : '';
  };


  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'machine_group') {
      const selectedMachine = machinesData.find(machine => machine.id.toString() === value);

      if (selectedMachine) {
        const productionDate = formData.production_date ? new Date(formData.production_date) : new Date();
        const fromTime = selectedMachine.from_time || '8:00:00';
        const fromDate = combineDateWithTime(productionDate, fromTime);
        const nextDay = new Date(productionDate);
        nextDay.setDate(nextDay.getDate() + 1);
        const toTime = selectedMachine.to_time || '8:00:00';
        const toDate = combineDateWithTime(nextDay, toTime);

        setFormData(prevData => ({
          ...prevData,
          machine_group: value,
          from_date_time: formatDateForInput(fromDate),
          to_date_time: formatDateForInput(toDate)
        }));
        return;
      }
    } else if (name === 'production_date') {
      if (formData.machine_group) {
        const selectedMachine = machinesData.find(machine => machine.id.toString() === formData.machine_group);
        if (selectedMachine) {
          const productionDate = value ? new Date(value) : new Date();
          const fromTime = selectedMachine.from_time || '8:00:00';
          const fromDate = combineDateWithTime(productionDate, fromTime);
          const nextDay = new Date(productionDate);
          nextDay.setDate(nextDay.getDate() + 1);
          const toTime = selectedMachine.to_time || '8:00:00';
          const toDate = combineDateWithTime(nextDay, toTime);

          setFormData(prevData => ({
            ...prevData,
            production_date: value,
            from_date_time: formatDateForInput(fromDate),
            to_date_time: formatDateForInput(toDate)
          }));
          return;
        }
      }
    }

    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const combineDateWithTime = (date, timeString) => {
    const [hours, minutes, seconds] = timeString.split(':');
    const newDate = new Date(date);
    newDate.setHours(parseInt(hours), parseInt(minutes), parseInt(seconds));
    return newDate;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.machine_group) {
      alert("Please select a department.");
      return;
    }

    if (!formData.from_date_time || !formData.to_date_time || !formData.production_date) {
      alert('Please fill in all date/time fields');
      return;
    }

    const formattedFromDateTime = formData.from_date_time.replace('T', ' ');
    const formattedToDateTime = formData.to_date_time.replace('T', ' ');

    const productionDataToSend = {
      production_date: formData.production_date,
      from_date_time: formattedFromDateTime,
      to_date_time: formattedToDateTime,
      machine_group: formData.machine_group
    };
    const token = sessionStorage.getItem('token');
    axios.post(`${BaseURL}meters/production/`, productionDataToSend, {
      headers: {
        'Authorization': `Token ${token}`
      }
    })
      .then(() => {
        setFormData({
          from_date_time: '',
          to_date_time: '',
          machine_group: '',
          production_date: ''
        });
        setIsAddModalVisible(false);
        setSuccessMessage('Production data added successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        if (fromDate || toDate) {
          fetchProductionData(1, fromDate, toDate);
        } else {
          fetchInitialProductionData();
        }
      })
      .catch(error => {
        console.error('Error adding production data:', error);
        if (error.response) {
          if (error.response.status === 400) {
            if (error.response.data.message === "Already Exist") {
              alert('Production data for this date and department already exists');
            } else {
              alert(error.response.data.error || 'Invalid data. Please check your inputs.');
            }
          } else if (error.response.status === 403) {
            alert("You don't have permission to add production data");
          } else {
            alert('An error occurred while adding production data');
          }
        } else {
          alert('Network error. Please try again later.');
        }
      })
      .finally(() => {
        setTimeout(() => setSuccessMessage(''), 3000);
      });
  };

  const closeAddModal = () => {
    setIsAddModalVisible(false);
    setFormData({
      from_date_time: '',
      to_date_time: '',
      machineGroup: '',
      production_date: ''
    });
  };


  const showAddModal = () => {
    setIsAddModalVisible(true);
  };

  const handleDelete = () => {
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    const token = sessionStorage.getItem('token');

    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }

    try {
      const deleteData = productionData
        .filter(item => selectedRows.includes(item.id))
        .map(item => ({
          production_date: item.Date,
          department: machinesData.find(m => m.machine_name === item.Department)?.id
        }));

      await axios.delete(`${BaseURL}meters/production/bulkdelete/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        data: deleteData
      });

      setProductionData(prevData =>
        prevData.filter(item => !selectedRows.includes(item.id))
      );
      setSelectedRows([]);
      setDeleteModalOpen(false);
      if (fromDate || toDate) {
          fetchProductionData(1, fromDate, toDate);
        } else {
          fetchInitialProductionData();
        }
      setSuccessMessage(
        `Successfully deleted ${selectedRows.length} production record(s)!`
      );
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error("Error deleting rows:", error);
      alert(
        error.response?.data?.message ||
        `Failed to delete records. Please try again.`
      );
      setDeleteModalOpen(false);
    }
  };

  const handleProductionValueClick = (productionItem, productName) => {
    const product = productsData.find(p => p.product_name === productName);
    if (!product) return;

    const productionValue = productionItem[productName] || '';
    let actualValue = productionValue.includes('/') 
      ? productionValue.split('/')[0].trim() 
      : productionValue.trim();
    actualValue = actualValue.replace(/[^\d.]/g, '');

    setEditFormData({
      id: productionItem.id,
      production_date: productionItem.Date,
      machine_group: machinesData.find(m => m.machine_name === productionItem.Department)?.id,
      from_date_time: productionItem.From,
      to_date_time: productionItem.To,
      product: product.id,
      product_name: productName,
      actual_production: actualValue
    });
    setEditType('production');
    setIsEditModalVisible(true);
  };

  const handleDateTimeClick = (productionItem, field) => {
    setEditFormData({
      id: productionItem.id,
      production_date: productionItem.Date,
      machine_group: machinesData.find(m => m.machine_name === productionItem.Department)?.id,
      from_date_time: productionItem.From.replace(' ', 'T') + ':00',
      to_date_time: productionItem.To.replace(' ', 'T') + ':00',
      product: null,
      product_name: '',
      actual_production: '',
      editingField: field
    });
    setEditType('datetime');
    setIsEditModalVisible(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProductionValueUpdate = async (updateData) => {
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.put(
        `${BaseURL}meters/production/${updateData.id}/`,
        updateData,
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const handleDateTimeUpdate = async (updateData) => {
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.put(
        `${BaseURL}meters/production/updatedate/`,
        updateData,
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editType === 'production') {
        const updateData = {
          id: editFormData.id,
          production_date: editFormData.production_date,
          machine_group: editFormData.machine_group,
          product: editFormData.product,
          actual_production: parseFloat(editFormData.actual_production)
        };

        await handleProductionValueUpdate(updateData);

        setProductionData(prevData =>
          prevData.map(item => {
            if (item.id === editFormData.id) {
              const currentValue = item[editFormData.product_name] || '- / -';
              const [, targetValue] = currentValue.split('/');

              return {
                ...item,
                [editFormData.product_name]: `${editFormData.actual_production} / ${targetValue ? targetValue.trim() : '-'}`
              };
            }
            return item;
          })
        );
      } else {
        const formatDateTime = (datetimeStr) => {
          const dt = datetimeStr.replace('T', ' ');
          return dt.includes(':') ? 
            (dt.split(':').length === 2 ? `${dt}:00` : dt) : 
            `${dt}:00:00`;
        };
        const updateData = {
          production_date: editFormData.production_date,
          department: editFormData.machine_group,
          from_date_time: formatDateTime(editFormData.from_date_time),
          to_date_time: formatDateTime(editFormData.to_date_time)
        };

        const response = await handleDateTimeUpdate(updateData);

        if (response.message) {
          setSuccessMessage(response.message);
        }

        setProductionData(prevData =>
          prevData.map(item => {
            if (item.id === editFormData.id) {
              return {
                ...item,
                From: updateData.from_date_time.split(' ')[1].substring(0, 5),
                To: updateData.to_date_time.split(' ')[1].substring(0, 5)
              };
            }
            return item;
          })
        );
      }

      setIsEditModalVisible(false);
      setTimeout(() => setSuccessMessage(''), 3000);
      if (fromDate || toDate) {
        fetchProductionData(1, fromDate, toDate);
      } else {
        fetchInitialProductionData();
      }
    } catch (error) {
      console.error('Error updating production data:', error);
      let errorMessage = 'Failed to update production data';
      if (error.response) {
        if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 403) {
          errorMessage = "You don't have permission to edit this production data";
        }
      }
      alert(errorMessage);
    }
  };

  const closeEditModal = () => {
    setIsEditModalVisible(false);
    setEditFormData({
      id: null,
      production: {},
      production_date: '',
      machine_group: ''
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(productionData.map(item => item.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleCheckboxChange = (id) => {
    setSelectedRows(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const isAllSelected = selectedRows.length === productionData.length && productionData.length > 0;

  const lightenColor = (color, percent) => {
    if (!color || color === '#FFFFFF' || color.length !== 7) return '#f0f0f0';
    
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    
    const R = Math.min(255, ((num >> 16) + amt));
    const G = Math.min(255, (((num >> 8) & 0x00FF) + amt));
    const B = Math.min(255, ((num & 0x0000FF) + amt));
    
    return `#${((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1)}`;
  };

  const handleExportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Production Data');

    const getBase64FromImageUrl = async (url) => {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const columnCount = 5 + productsList.length + 2;

    for (let rowNum = 1; rowNum <= 3; rowNum++) {
        const row = worksheet.getRow(rowNum);
        for (let i = 1; i <= columnCount; i++) {
            row.getCell(i).fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFDDDDDD' },
            };
        }
    }

    try {
        const logoBase64 = await getBase64FromImageUrl('/brad.png');
        const logoId = workbook.addImage({
            base64: logoBase64,
            extension: 'png'
        });

        const centerCol = Math.floor(columnCount / 2) - 1;
        worksheet.addImage(logoId, {
            tl: { col: centerCol + 1, row: 0 },
            br: { col: centerCol + 2, row: 3 },
            editAs: 'oneCell'
        });

        worksheet.getRow(1).height = 30;
        worksheet.getRow(2).height = 30;
        worksheet.getRow(3).height = 30;
    } catch (error) {
        console.error('Error adding logo:', error);
    }

    const titleRow = worksheet.addRow(['Production Data Report']);
    titleRow.font = { bold: true, size: 16 };
    titleRow.alignment = { horizontal: 'center' };
    worksheet.mergeCells(titleRow.number, 1, titleRow.number, columnCount);
    titleRow.eachCell(cell => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF1E90FF' },
        };
    });

    const dateRangeText = `Generated on: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
    const dateRangeRow = worksheet.addRow([dateRangeText]);
    dateRangeRow.font = { bold: true, size: 14 };
    dateRangeRow.alignment = { horizontal: 'center' };
    worksheet.mergeCells(dateRangeRow.number, 1, dateRangeRow.number, columnCount);
    dateRangeRow.eachCell(cell => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD9EAD3' },
        };
    });

    const headers = [
        'S.No',
        'Date', 
        'From', 
        'To', 
        'Department',
        ...productsList.map(product => product.name || product),
        'Total kWh',
        'Total Cost'
    ];
    
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell(cell => {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFA500' },
        };
    });

    if (productionData && productionData.length > 0) {
        productionData.forEach((item, index) => {
            const rowData = [
                index + 1,
                item.Date,
                item.From,
                item.To,
                item.Department,
            ];

            productsList.forEach(product => {
                const productKey = product.key || product;
                rowData.push(item[productKey] || '-');
            });

            rowData.push(
              item.kWh ? Math.round(parseFloat(item.kWh)) : '0',
              item.Cost ? Math.round(parseFloat(item.Cost)) : '0'
            );

            const dataRow = worksheet.addRow(rowData);
            dataRow.eachCell(cell => {
                const fillColor = index % 2 === 0 ? 'FFE8EAF6' : 'FFFFFFFF';
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: fillColor },
                };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });
        });
    } else {
        const noDataRow = worksheet.addRow(['No data available']);
        noDataRow.font = { italic: true };
        worksheet.mergeCells(noDataRow.number, 1, noDataRow.number, columnCount);
    }

    const footerRow = worksheet.addRow(['Powered by Innospace']);
    footerRow.font = { bold: true, size: 12 };
    footerRow.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.mergeCells(footerRow.number, 1, footerRow.number, columnCount);
    footerRow.eachCell(cell => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFDDDDDD' },
        };
    });

    const columnWidths = [
        8,
        15,
        10,
        10,
        20,
        ...productsList.map(() => 25),
        12,
        12
    ];

    columnWidths.forEach((width, index) => {
        worksheet.getColumn(index + 1).width = width;
    });

    workbook.xlsx.writeBuffer().then(buffer => {
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Production_Data_${new Date().toISOString().split('T')[0]}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    }).catch(error => {
        console.error('Error exporting to Excel:', error);
    });
  };

  return (
    <div style={{ textAlign: 'center', width: '100%' }}>
      <div style={{
        backgroundColor: '#007acc',
        color: 'white',
        alignContent: 'center',
        fontSize: '14px',
        height: '40px'
      }}>
        <Header title="PRODUCTION DETAILS" />
      </div>

      <div style={{
        border: '1px solid #ccc',
        padding: '10px',
        marginTop: '20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        backgroundColor: 'white',
        margin: '20px auto',
        maxWidth: '1000px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', width: '60%' }}>
            <select
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                flex: '1',
                marginRight: '10px',
              }}
            >
              <option value="">All Machines</option>
              {machinesData.map(machine => (
                <option key={machine.id} value={machine.id}>
                  {machine.machine_name}
                </option>
              ))}
            </select>
            <FontAwesomeIcon
              icon={faSearch}
              style={{ 
                color: '#007acc', 
                cursor: 'pointer', 
                marginRight: '10px',
                padding: '10px',
                backgroundColor: '#f0f0f0',
                borderRadius: '8px'
              }}
              onClick={handleSearchClick}
            />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                marginRight: '10px',
              }}
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                marginRight: '10px',
              }}
            />
            {(fromDate || toDate || searchQuery) && (
              <button
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                  setSearchQuery("");
                  fetchInitialProductionData();
                }}
                style={{
                  padding: '10px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Clear All
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              style={{
                padding: '10px 15px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                marginRight: '10px',
              }}
              onClick={showAddModal}
            >
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: '5px' }} />
              Add
            </button>
            {isSuperuser && (
              <>
                <button
                  style={{
                    padding: '10px 15px',
                    marginRight: '10px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (selectedRows.length > 0) {
                      handleDelete();
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} style={{ marginRight: '5px' }} />
                  Delete
                </button>
                <button
                  style={{
                    padding: '10px 15px',
                    backgroundColor: '#007acc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                  onClick={handleExportToExcel}
                >
                  Download
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'center', margin: '20px 0' }}>
        {successMessage && (
          <div style={{
            padding: '10px',
            borderRadius: '5px',
            backgroundColor: '#4CAF50',
            color: 'white',
            margin: '10px auto',
            width: 'fit-content',
          }}>
            {successMessage}
          </div>
        )}
      </div>

      <div style={{
        marginTop: '20px',
        padding: '10px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        backgroundColor: 'white',
        maxWidth: '1000px',
        margin: '20px auto',
        marginBottom: '70px',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse', minWidth: '800px' }}>
            <thead style={{ backgroundColor: '#007acc', color: 'white' }}>
              <tr>
                {isSuperuser && (
                  <th style={{ padding: '10px' }}>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={isAllSelected}
                      ref={el => {
                        if (el) {
                          el.indeterminate = 
                            selectedRows.length > 0 && 
                            !isAllSelected;
                        }
                      }}
                    />
                  </th>
                )}
                <th style={{ padding: '10px' }}>Date</th>
                <th style={{ padding: '10px' }}>From</th>
                <th style={{ padding: '10px' }}>To</th>
                <th style={{ padding: '10px' }}>Department</th>
                {productsList.map(product => (
                  <th key={product} style={{ padding: '10px' }}>{product}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {productionData.map((item, index) => (
                <tr key={index} style={{
                  backgroundColor: index % 2 === 0 ? '#f2f2f2' : '#ffffff',
                }}>
                  {isSuperuser && (
                    <td style={{ padding: '10px' }}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(item.id)}
                        onChange={() => handleCheckboxChange(item.id)}
                      />
                    </td>
                  )}
                  <td style={{ padding: '10px' }}>{item.Date}</td>
                  <td 
                    style={{ padding: '10px', cursor: 'pointer' }}
                    onClick={() => handleDateTimeClick(item, 'from')}
                  >
                    {item.From}
                  </td>
                  <td 
                    style={{ padding: '10px', cursor: 'pointer' }}
                    onClick={() => handleDateTimeClick(item, 'to')}
                  >
                    {item.To}
                  </td>
                  <td style={{ padding: '10px' }}>
                    {(() => {
                      const machine = machinesData.find(m => m.machine_name === item.Department);
                      const originalColor = machine?.color || '#ff5252';
                      const lightenedColor = lightenColor(originalColor, 25);
                      
                      return (
                        <span style={{
                          backgroundColor: lightenedColor,
                          color: '#000000',
                          fontSize: '14px',
                          border: `2px solid ${originalColor}`,
                          borderRadius: '15px',
                          padding: '0px 8px',
                          display: 'inline-block',
                          flex: 1,
                          marginRight: '5px',
                          textAlign: 'center'
                        }}>
                          {item.Department}
                        </span>
                      );
                    })()}
                  </td>
                  {productsList.map(productName => {
                    const value = item[productName] || '-';
                    const displayValue = value.includes('/') 
                      ? value.split('/')[0].trim()
                      : value === '-' 
                        ? '-' 
                        : `${Math.round(parseFloat(value.trim()))} ${getProductUnit(productName)}`;
                        
                    return (
                      <td
                        key={productName}
                          style={{ padding: '10px', cursor: displayValue === '-' ? 'default' : 'pointer' }}
                          onClick={() => {
                            if (displayValue !== '-') {
                              handleProductionValueClick(item, productName);
                            }
                          }}
                      >
                        {displayValue}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={!pagination.previous}
            style={{
              margin: '0 5px',
              padding: '5px 10px',
              background: !pagination.previous ? '#cccccc' : '#007acc',
              color: !pagination.previous ? '#666666' : '#ffffff',
              border: 'none',
              borderRadius: '25px',
              cursor: !pagination.previous ? 'not-allowed' : 'pointer',
              fontSize: '12px',
            }}
          >
            Previous
          </button>

          {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                style={{
                  margin: '0 5px',
                  padding: '5px 10px',
                  background: pagination.current_page === page ? '#007acc' : '#f0f0f0',
                  color: pagination.current_page === page ? '#ffffff' : '#333333',
                  border: 'none',
                  borderRadius: '25px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                {page}
              </button>
            ))}

          <button
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={!pagination.next}
            style={{
              margin: '0 5px',
              padding: '5px 10px',
              background: !pagination.next ? '#cccccc' : '#007acc',
              color: !pagination.next ? '#666666' : '#ffffff',
              border: 'none',
              borderRadius: '25px',
              cursor: !pagination.next ? 'not-allowed' : 'pointer',
              fontSize: '12px',
            }}
          >
            Next
          </button>
        </div>
      </div>
      {isAddModalVisible && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999,
          animation: 'fadeIn 0.3s ease-out',
        }}>
          <div ref={modalRef} style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            width: '650px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            transform: 'scale(1.05)',
            animation: 'zoomIn 0.4s ease-in-out',
          }}>
            <h3 style={{
              fontSize: '24px',
              color: '#333',
              fontFamily: 'Arial, sans-serif',
              marginBottom: '20px',
              textAlign: 'center',
            }}>
              Add New Production Data
            </h3>
            <form>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '20px',
              }}>
                <div>
                  <label style={{
                    fontWeight: '600',
                    color: '#444',
                    marginBottom: '8px',
                  }}>Production Date</label>
                  <input
                    type="date"
                    name="production_date"
                    value={formData.production_date}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      boxSizing: 'border-box',
                      fontSize: '16px',
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    fontWeight: '600',
                    color: '#444',
                    marginBottom: '8px',
                  }}>Department</label>
                  <select
                    name="machine_group"
                    value={formData.machine_group}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      fontSize: '16px',
                    }}
                  >
                    <option value="">Select a Department</option>
                    {machinesData.map((machine) => (
                      <option key={machine.id} value={machine.id}>
                        {machine.machine_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{
                    fontWeight: '600',
                    color: '#444',
                    marginBottom: '8px',
                  }}>From Date Time</label>
                  <input
                    type="datetime-local"
                    name="from_date_time"
                    value={formData.from_date_time}
                    onChange={handleChange}
                    disabled={!isSuperuser}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      boxSizing: 'border-box',
                      fontSize: '16px',
                      backgroundColor: isSuperuser ? '#fff' : '#f5f5f5',
                      cursor: isSuperuser ? 'text' : 'not-allowed',
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    fontWeight: '600',
                    color: '#444',
                    marginBottom: '8px',
                  }}>To Date Time</label>
                  <input
                    type="datetime-local"
                    name="to_date_time"
                    value={formData.to_date_time}
                    onChange={handleChange}
                    disabled={!isSuperuser}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      boxSizing: 'border-box',
                      fontSize: '16px',
                      backgroundColor: isSuperuser ? '#fff' : '#f5f5f5',
                      cursor: isSuperuser ? 'text' : 'not-allowed',
                    }}
                  />
                </div>
              </div>
              <div style={{
                marginTop: '20px',
                display: 'flex',
                justifyContent: 'space-between',
              }}>
                <button
                  onClick={handleSubmit}
                  style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    transition: 'background-color 0.3s',
                    width: '20%',
                    marginLeft: '210px'
                  }}
                >
                  Save
                </button>
                <button
                  onClick={closeAddModal}
                  style={{
                    backgroundColor: '#ff4d4d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    transition: 'background-color 0.3s',
                    width: '20%',
                    marginRight: '150px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#e04040'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#ff4d4d'}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isEditModalVisible && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999,
        }}>
          <div ref={editModalRef} style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            width: '400px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          }}>
            <h3 style={{
              fontSize: '24px',
              color: '#333',
              marginBottom: '20px',
              textAlign: 'center',
            }}>
              Edit {editFormData.product_name} Production
            </h3>

            <form onSubmit={handleEditSubmit}>
              <input type="hidden" name="production_date" value={editFormData.production_date} />
              <input type="hidden" name="machine_group" value={editFormData.machine_group} />
              <input type="hidden" name="product" value={editFormData.product} />

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  color: '#444',
                  marginBottom: '8px',
                }}>
                  Production Value ({productsData.find(p => p.id === editFormData.product)?.weight_unit || ''})
                </label>
                <input
                  type="number"
                  step="1"
                  name="actual_production"
                  value={editFormData.actual_production || ''}
                  onChange={handleEditChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '16px',
                  }}
                />
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}>
                <button
                  type="submit"
                  style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    width: '48%',
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={closeEditModal}
                  style={{
                    backgroundColor: '#ff4d4d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    width: '48%',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isEditModalVisible && editType === 'datetime' && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999,
        }}>
          <div ref={editModalRef} style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            width: '400px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          }}>
            <h3 style={{
              fontSize: '24px',
              color: '#333',
              marginBottom: '20px',
              textAlign: 'center',
            }}>
              Edit {editFormData.editingField === 'from' ? 'From' : 'To'} Date/Time
            </h3>

            <form onSubmit={handleEditSubmit}>
              <input type="hidden" name="production_date" value={editFormData.production_date} />
              <input type="hidden" name="machine_group" value={editFormData.machine_group} />
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  color: '#444',
                  marginBottom: '8px',
                }}>
                  {editFormData.editingField === 'from' ? 'From' : 'To'} Date/Time
                </label>
                <input
                  type="datetime-local"
                  name={editFormData.editingField === 'from' ? 'from_date_time' : 'to_date_time'}
                  value={editFormData[editFormData.editingField === 'from' ? 'from_date_time' : 'to_date_time']}
                  onChange={handleEditChange}
                  required
                  step="1"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '16px',
                  }}
                />
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}>
                <button
                  type="submit"
                  style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    width: '48%',
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={closeEditModal}
                  style={{
                    backgroundColor: '#ff4d4d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    width: '48%',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isDeleteModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div ref={deleteModalRef}
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "10px",
              width: "400px",
              textAlign: "center",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete the selected ProductionDetails?</p>
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "space-around",
              }}
            >
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  style={{
                    padding: "10px 15px",
                    color: "white",
                    backgroundColor: "#4CAF50",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                  onClick={confirmDelete}
                >
                  Yes, Delete
                </button>
                <button
                  style={{
                    padding: "10px 15px",
                    color: "white",
                    backgroundColor: "#f44336",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                  onClick={() => setDeleteModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionDetails;